/* ------------------------------------------------------------------ *
 * Icone degli sport e bandiere dei paesi
 *
 * Da dove viene la scelta, in ordine:
 *
 *   1. la CHIAVE che manda il catalogo nel campo `icon` — 'calcio',
 *      'basket', 'hockey'... E' questa la strada buona: la decide il
 *      backoffice, non il sito, e non si rompe se il fornitore cambia
 *      lingua o rinomina lo sport.
 *   2. se il catalogo manda ancora un percorso (`/assets/sport.svg`,
 *      che oggi e' lo stesso identico per TUTTI gli sport e tutte le
 *      categorie — ed e' il motivo per cui si vede sempre lo stesso
 *      pallone), si ripiega sul nome dello sport;
 *   3. se non si riconosce nemmeno il nome, un'icona neutra. Mai niente:
 *      una riga senza icona si vede piu' di una con l'icona sbagliata.
 *
 * Il punto 2 e' una RETE, non la regola: vive qui e non nel catalogo
 * apposta, cosi' il giorno che il backoffice manda le chiavi si spegne
 * da sola senza toccare niente.
 * ------------------------------------------------------------------ */

import {
  faFutbol,
  faBasketball,
  faHockeyPuck,
  faBaseballBatBall,
  faFootball,
  faHandFist,
  faVolleyball,
  faTableTennisPaddleBall,
  faGolfBallTee,
  faPersonSkiing,
  faPersonSwimming,
  faPersonBiking,
  faCarSide,
  faHorseHead,
  faBowlingBall,
  faBullseye,
  faGamepad,
  faPersonRunning,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';

/* Quelle che FontAwesome non ha le disegniamo noi: stanno in
   IconaSport.js perche' sono forme, non dati. Il tennis e' qui perche'
   FontAwesome gratuito ha solo la racchetta del ping-pong, e il tennis si
   vedeva con quella. */
export const DISEGNATE = ['cricket', 'rugby', 'aussie', 'pallamano', 'tennis'];

/* chiave -> icona. La chiave e' quella che dovra' mandare il catalogo. */
const PER_CHIAVE = {
  calcio: faFutbol,
  basket: faBasketball,
  hockey: faHockeyPuck,
  baseball: faBaseballBatBall,
  football: faFootball,
  mma: faHandFist,
  pugilato: faHandFist,
  pallavolo: faVolleyball,
  pingpong: faTableTennisPaddleBall,
  tennis: faTableTennisPaddleBall, // ripiego: il disegno vero sta in IconaSport
  golf: faGolfBallTee,
  sci: faPersonSkiing,
  nuoto: faPersonSwimming,
  ciclismo: faPersonBiking,
  motori: faCarSide,
  ippica: faHorseHead,
  bowling: faBowlingBall,
  freccette: faBullseye,
  esport: faGamepad,
  atletica: faPersonRunning,
  generico: faTrophy,
};

/* La rete: nome dello sport (come arriva dal fornitore) -> chiave.
   Si toglie il giorno che il catalogo manda le chiavi. */
const PER_NOME = {
  'soccer': 'calcio',
  'football': 'calcio',
  'calcio': 'calcio',
  'basketball': 'basket',
  'basket': 'basket',
  'ice hockey': 'hockey',
  'hockey': 'hockey',
  'baseball': 'baseball',
  'american football': 'football',
  'boxing': 'pugilato',
  'mixed martial arts': 'mma',
  'mma': 'mma',
  'cricket': 'cricket',
  'aussie rules': 'aussie',
  'australian rules': 'aussie',
  'rugby league': 'rugby',
  'rugby union': 'rugby',
  'rugby': 'rugby',
  'handball': 'pallamano',
  'volleyball': 'pallavolo',
  'table tennis': 'pingpong',
  'tennis': 'tennis',
  'golf': 'golf',
  'darts': 'freccette',
  'snooker': 'bowling',
  'cycling': 'ciclismo',
  'motorsport': 'motori',
  'formula 1': 'motori',
  'horse racing': 'ippica',
  'esports': 'esport',
  'athletics': 'atletica',
};

function ripulisci(testo) {
  return String(testo || '').trim().toLowerCase();
}

/**
 * La chiave dell'icona di uno sport.
 * Torna sempre qualcosa: 'generico' quando non si riconosce niente.
 */
export function chiaveSport(sport) {
  if (!sport) return 'generico';

  /* 1. la chiave dal catalogo. Un percorso non e' una chiave: oggi
        `icon` contiene `/assets/sport.svg` per tutti. */
  const dalCatalogo = ripulisci(sport.icon);
  if (dalCatalogo && dalCatalogo.indexOf('/') === -1 &&
      dalCatalogo.indexOf('.') === -1) {
    if (PER_CHIAVE[dalCatalogo] || DISEGNATE.indexOf(dalCatalogo) !== -1) {
      return dalCatalogo;
    }
  }

  /* 2. la rete sul nome */
  const dalNome = PER_NOME[ripulisci(sport.name)];
  if (dalNome) return dalNome;

  return 'generico';
}

/** L'icona FontAwesome di una chiave, o null se e' una di quelle disegnate. */
export function iconaDiChiave(chiave) {
  return PER_CHIAVE[chiave] || null;
}

/* ------------------------------------------------------------------ *
 * Bandiere
 *
 * Serve un codice paese di due lettere, e deve arrivare dal catalogo:
 * dal nome non si puo' ricavare, perche' i nomi arrivano in lingue
 * miste ("Germania" e "Svezia" in italiano, "Argentina" e "Austria"
 * uguali in tutte e due) e cambiano quando cambia la lingua del sito.
 *
 * Le categorie non sono tutte paesi: NBA, NHL, Euroleague, MLB e le
 * coppe UEFA sono competizioni. Quelle il codice non ce l'hanno, e al
 * loro posto si mette l'icona dello sport.
 * ------------------------------------------------------------------ */

/** Il codice paese di una categoria, in minuscolo, o null. */
export function codicePaese(categoria) {
  if (!categoria) return null;
  const grezzo = categoria.paese || categoria.country ||
    categoria.countryCode || categoria.codicePaese;
  const codice = ripulisci(grezzo);
  return /^[a-z]{2}$/.test(codice) ? codice : null;
}

/**
 * La bandiera come emoji, ricavata dal codice.
 * Due lettere -> due "indicatori regionali": 'de' -> 🇩🇪. Nessun file da
 * caricare e vale per qualsiasi paese. Su Windows il carattere non
 * esiste e si vede il codice: per quello chi disegna la bandiera prova
 * prima il file in /assets/bandiere/<codice>.svg, se c'e'.
 */
export function bandieraEmoji(codice) {
  if (!codice) return '';
  const base = 0x1f1e6;
  return String.fromCodePoint(
      base + codice.charCodeAt(0) - 97,
      base + codice.charCodeAt(1) - 97,
  );
}
